import { useEffect, useState } from "react";
import Loader from "components/Loader";
import { Button } from "../Button";
import TextBox from "../TextBox";
import ShoppingCartService from "../services/shopping-cart.service";
import { SupplyOwnFabric } from "../SupplyOwnFabric";
import ProductQtyInput from "components/ProductQtyInput";
import { OtherFabricSelector } from "../OtherFabricSelector";
import { useErrorBoundary } from "basket/ErrorBoundary";
import { formatCurrency } from "utils/utils";
import useInfoIcons from "hooks/useInfoIcons";
import InformationOverlay from "../InformationOverlay";

export function BasketEditBeanbagsItem(props) {
    const { item, updateFabricItem, close } = props;
    const handleError = useErrorBoundary();
    const [price, setPrice] = useState(item.price);
    const [metres, setMetres] = useState(item.fabric.fabricMetres.toFixed(1) + 'm');
    const [formData, setFormData] = useState({
        quantity: item.quantity,
        price: item.price,
        customization: item.fabric.customization,
        location: item.fabric.location,
        sessionId: item.productId,
        fabricId: item.fabricId,
        supplyOwnFabricNameOrDescription: item.supplyOwnFabricNameOrDescription,
        FabricRollWidth: item.supplyOwnFabricRollWidth,
        VerticalPatternRepeat: item.supplyOwnFabricPatternRepeat
    });

    const [isSubmitDisabled, setSubmitDisabled] = useState(false);
    const [isLoading, setLoading] = useState(false)
    const [errors, setErrors] = useState({});
    
    const [fabrics, setFabrics] = useState([])
    const [totalFabricsCount, setTotalFabricsCount] = useState(0)
    const [selectedFabric, setSelectedFabric] = useState(formData.fabricId)

    const [itemTitle, setItemTitle] = useState(`${item.fabric.fabricName} in ${item.colour || ''} Fabric by ${item.fabric.supplier.name || ''}`);
    const title = () => item.supplyOwnFabric ? 'Edit Beanbags in Supply Your Own Fabric' : `Edit Beanbags - ${itemTitle}`;
                    
    const infoIcons = useInfoIcons(item.productType);

    const handleInputChange = (field, value) => {
        setFormData((prevData) => {
            return {
                ...prevData,
                [field]: value,
            }
        });
    };

    const handleInputChangeUniversal = (e) => {        
        const { name, value } = e.target;
        const updatedData = { ...formData, [name]: value };
        setFormData(updatedData);

        const validationError = validateInput(name, value);
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: validationError
        }));

        const hasErrors = Object.values({ ...errors, [name]: validationError }).some((error) => error);
        setSubmitDisabled(hasErrors);
    };

    const validateInput = (name, value) => {
        let error = '';
        const num = Number(value);
        const errorMessage = "Invalid data";

        if (name === 'supplyOwnFabricPatternRepeat' || name === 'supplyOwnFabricRollWidth') {
            if (!Number.isInteger(num) || num <= 0 || num > 999) {
                error = errorMessage;
            }
        } else if (name === 'supplyOwnFabricNameOrDescription') {
            if (!value || value.trim() === '') {
                error = "This field is required";
            }
        }
        return error;
    };

    const validatePrice = async (item, callback) => {
        try {
            const response = await fetch('/api/product/beanbags/validate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify(item)
            });

            setErrors({});

            if (!response.ok) {
                const json = await response.json();

                const mappedErrors = Object.entries(json).reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

                setErrors(prev => ({
                    ...prev,
                    ...mappedErrors
                }));

                return;
            }

            callback && callback(item);

        } catch (e) {
            handleError('Validation failed');
        }
    };

    const handleUpdate = async () => {
        setLoading(true);
        setSubmitDisabled(true);

        await updateFabricItem(createItem());

        setSubmitDisabled(false);
        setLoading(false);
    }

    const loadFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + formData.fabricId)
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const loadAllFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + formData.fabricId + '&showAll=true')
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const createItem = () => ({
        ...item,
        fabricId: selectedFabric,
        quantity: formData.quantity,
        fabric: {
            sessionId: formData.sessionId,
            fabricId: selectedFabric,
            unitOfMeasurement: false,
            IsPiping: false,
            Location: formData.location,
            customization: formData.customization,
            quantity: formData.quantity,
        },
        supplyOwnFabricNameOrDescription: formData.supplyOwnFabricNameOrDescription,
        supplyOwnFabricPatternRepeat: formData.VerticalPatternRepeat,
        supplyOwnFabricRollWidth: formData.FabricRollWidth
    });
    
    const updatePrice = async (item) => {
        try {
            const result = await ShoppingCartService.create('price', item);

            if (result) {
                setPrice(result.price);
                setMetres(result.fabricMetres.toFixed(1) + 'm');
            }

        } catch {
            handleError('Could not load item price. Please contact administrator');
        }
    };

    useEffect(() => {
        const foundFabric = fabrics.find(fabric => fabric.fabricId === selectedFabric);
        if (foundFabric){                    
            setItemTitle(foundFabric.fullName);
            validatePrice(createItem(), updatePrice);
        }
    }, [selectedFabric]);

    useEffect(() => {
        validatePrice(createItem(), updatePrice);
    }, [
        formData.quantity,
        selectedFabric,
        formData.customization,
        formData.location,
        formData.fabricId
    ]);

    useEffect(() => {
        loadFabrics()
    }, [])

    return (
        <Loader isLoading={isLoading}>
            <form className="p-32 pb-54 bg3 measurements-change active-group sm-p-20">
                <div className="row">
                    <div className="col">
                        <div className="subtitle1 mb-16 sm-text-24 default-text">{title() + ' '} 
                            {infoIcons && infoIcons.length > 0  && (
                                <span className="paragraph-info-icon-wrapper">                                        
                                    <InformationOverlay item={infoIcons.find(o => o.name === "BeanbagDescription")} />
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-auto ps-24">
                        <span 
                            className="curtains-icon curtains-icon-close size-24 show-btn hide-btn"
                            onClick={() => close()}></span>
                    </div>
                </div>

                { item.supplyOwnFabric && <SupplyOwnFabric fabricData={formData} errors={errors} handleInputChange={handleInputChangeUniversal} onBlur={() => validatePrice(createItem(), updatePrice)} /> }
                { !item.supplyOwnFabric && <OtherFabricSelector fabrics={fabrics} totalFabricsCount={totalFabricsCount} loadAllFabrics={loadAllFabrics} selectedFabric={selectedFabric} setSelectedFabric={setSelectedFabric} /> }

                {/* Customization Section */}
                <div>
                    <div className="body-text3 gray mt-16 mb-16 align-center gap_3">Beanbag Customisation:</div>
                    <div className="max-width-410 mb-16 sm-text-14">If you have special requests, write below or <a href="/contact-us" className="underline">contact us</a> <a href="tel:0345 8620743" className="underline">0345 8620743</a>.</div>
                    <div className="mb-16">
                        <TextBox
                            name="Customization"
                            value={formData.customization}
                            placeholder="e.g. Trim or borders"
                            onChange={(e) => handleInputChange('customization', e.target.value)}
                            onBlur={() => validatePrice(createItem(), updatePrice)}
                        />
                    </div>
                </div>
                <div className="dashed-line"></div>

                {/* Location Section */}
                <div>
                    <div className="body-text3 gray mt-16 mb-16 align-center gap_3">Beanbag Location:</div>
                    <div className="max-width-410 mb-16 sm-text-14">Let us know where your beanbag will be used, and we will include this in your order details.</div>
                    <div className="mb-16">
                        <TextBox
                            name="Location"
                            value={formData.location}
                            placeholder="e.g. Living room"
                            onChange={(e) => handleInputChange('location', e.target.value)}
                            onBlur={() => validatePrice(createItem(), updatePrice)}
                        />
                    </div>
                </div>
                <div className="dashed-line"></div>

                {
                    item.supplyOwnFabric && 
                        <div>
                            <div className="body-text3 gray mt-32 mb-8 align-center gap_3">Quantity of fabric per item to be supplied by you:</div>
                            <TextBox name='sof-fabric-length' value={metres} className='max-width-150 mb-16' />
                        </div>
                }

                {/* Total Price and Quantity */}
                <div>
                    <div className="subtitle2 mb-8 mt-16">TOTAL: <span className="body-text1">{formatCurrency(price, 2)}</span></div>
                    <div className="body-text6">Delivery for made-to-measure beanbags: {item.deliveryContent} weeks.</div>
                </div>

                <div className="mb-16 mt-32" id="updateProductQuantity">
                    <div className="dashed-line mb-24"></div>
                    <div className="body-text3 gray mb-16">Quantity:</div>
                    
                    <ProductQtyInput
                        digits={0}
                        initValue={formData.quantity} 
                        onlyInt={true} 
                        onChange={(value) => handleInputChange('quantity', value)} 
                        onBlur={() => validatePrice(createItem(), updatePrice)}
                    />
                    {errors.Quantity && (
                        <span className="field-validation-error">{errors.Quantity}</span>
                    )}
                </div>

                <div className="d-flex gap-16 mb-16 sm-column md-column sm-gap-8 sm-mb-0">
                    <div className="w-100 show-btn hide-btn">
                        <Button name="UPDATE" onClick={async () => await handleUpdate()} disabled={isSubmitDisabled || Object.keys(errors).length > 0} />
                    </div>
                </div>
            </form>
        </Loader>
    );
}